/**
 * udf-writer.js — window.__DiskImage.UdfBuilder
 *
 * A UDF 1.02 filesystem writer, in the browser. It exists because a DVD-Video
 * image is not readable without one: libdvdread refuses an ISO 9660-only image
 * outright («Invalid UDF DVD. (Found ISO9660…)»), which is why /surround-disc
 * could only ever hand over a ZIP of VIDEO_TS and leave the filesystem to the
 * burning program. With this, the tool emits one .iso that a burner writes
 * as-is and a DVD engine navigates.
 *
 * Scope is deliberately small — read-only, everything known up front, no
 * rewriting, no free-space management, no extended attributes. That is exactly
 * what an image being burned once needs, and it keeps the module to something
 * that can be verified byte by byte.
 *
 * What a reader actually walks (dvd_udf.c is the reference implementation):
 *   sector 256 → Anchor Volume Descriptor Pointer
 *     → Main Volume Descriptor Sequence: Partition Descriptor (5) gives the
 *       partition's start and length, Logical Volume Descriptor (6) gives the
 *       block size, the «*OSTA UDF Compliant» domain and, in its contents-use
 *       field, the long_ad of the File Set Descriptor
 *     → File Set Descriptor (256): its long_ad at offset 400 is the root ICB
 *     → File Entry (261, file type 4 = directory)
 *     → File Identifier Descriptors (257) name the children
 * Everything below builds precisely that chain.
 *
 * Values are little-endian (ECMA-167), which is the opposite of the DVD-Video
 * IFO structures next door in dvd-video-author.js — a mix-up there produces a
 * volume that mounts but has garbage sizes, so every writer here is explicit.
 */
(function (w) {
    'use strict';
    if (w.__DiskImage && w.__DiskImage.UdfBuilder) return;

    var BLOCK = 2048;
    // ECMA-167 caps one extent at 0x3FFFFFFF bytes; UDF additionally requires
    // every extent but the last to be a whole number of blocks, so the usable
    // maximum is that value rounded down to a block boundary. A 1 GiB VOB
    // crosses it, so multi-extent files are not optional.
    var MAX_EXTENT = 0x3FFFF800;

    // ── primitives ─────────────────────────────────────────────────────────
    function u8w(b, o, v) { b[o] = v & 255; }
    function u16(b, o, v) { b[o] = v & 255; b[o + 1] = (v >>> 8) & 255; }
    function u32(b, o, v) { b[o] = v & 255; b[o + 1] = (v >>> 8) & 255; b[o + 2] = (v >>> 16) & 255; b[o + 3] = (v >>> 24) & 255; }
    function u64(b, o, v) { u32(b, o, v >>> 0); u32(b, o + 4, Math.floor(v / 4294967296)); }
    function ascii(b, o, s, len) {
        for (var i = 0; i < len; i++) b[o + i] = i < s.length ? (s.charCodeAt(i) & 127) : 0;
    }

    /* CRC-16/CCITT-FALSE over the descriptor body — the ITU-T polynomial UDF
       uses for every tag. A wrong CRC is the difference between "mounts" and
       "not a UDF volume", and it is not visible in a hex dump, so it is worth
       generating the table rather than trusting a constant. */
    var CRC16 = (function () {
        var t = new Uint16Array(256);
        for (var i = 0; i < 256; i++) {
            var c = i << 8;
            for (var k = 0; k < 8; k++) c = (c & 0x8000) ? ((c << 1) ^ 0x1021) : (c << 1);
            t[i] = c & 0xffff;
        }
        return t;
    })();
    function crc16(b, off, len) {
        var c = 0;
        for (var i = 0; i < len; i++) c = ((c << 8) ^ CRC16[((c >> 8) ^ b[off + i]) & 0xff]) & 0xffff;
        return c;
    }

    /* Descriptor tag. Written LAST for each structure, because both the
       checksum and the CRC cover bytes that only exist once the body is done. */
    function tag(b, off, id, location, crcLength) {
        u16(b, off, id);
        u16(b, off + 2, 2);                        // descriptor version 2 = UDF 1.02
        b[off + 4] = 0;                            // checksum, filled below
        b[off + 5] = 0;
        u16(b, off + 6, 0);                        // tag serial number
        u16(b, off + 8, crc16(b, off + 16, crcLength));
        u16(b, off + 10, crcLength);
        u32(b, off + 12, location);
        var sum = 0;
        for (var i = 0; i < 16; i++) if (i !== 4) sum += b[off + i];
        b[off + 4] = sum & 255;
    }

    /* OSTA compressed unicode. Everything we write is ASCII, so compression
       ID 8 applies; the LAST byte of the field carries the used length, which
       is the part that is easy to forget and makes names come out empty. */
    function dstring(b, off, s, fieldLen) {
        s = String(s || '');
        var max = fieldLen - 2;
        if (s.length > max) s = s.slice(0, max);
        if (!s.length) { for (var z = 0; z < fieldLen; z++) b[off + z] = 0; return; }
        b[off] = 8;
        for (var i = 0; i < s.length; i++) b[off + 1 + i] = s.charCodeAt(i) & 255;
        for (var p = 1 + s.length; p < fieldLen - 1; p++) b[off + p] = 0;
        b[off + fieldLen - 1] = s.length + 1;
    }
    function charspec(b, off) {
        b[off] = 0;                                 // CS0
        ascii(b, off + 1, 'OSTA Compressed Unicode', 63);
    }
    /* Entity identifier. The 8-byte suffix of a UDF-defined id carries the
       revision (0x0102 = UDF 1.02) — readers check it for the domain id. */
    function regid(b, off, id, flags, udfSuffix) {
        b[off] = flags || 0;
        ascii(b, off + 1, id, 23);
        for (var i = 0; i < 8; i++) b[off + 24 + i] = 0;
        if (udfSuffix) { u16(b, off + 24, 0x0102); b[off + 26] = 0; b[off + 27] = 0; }
        else { ascii(b, off + 24, 'TIMBRICA', 8); }
    }
    function timestamp(b, off, d) {
        u16(b, off, 0x1000);                        // type 1 (local), offset 0
        u16(b, off + 2, d.getUTCFullYear());
        b[off + 4] = d.getUTCMonth() + 1; b[off + 5] = d.getUTCDate();
        b[off + 6] = d.getUTCHours(); b[off + 7] = d.getUTCMinutes(); b[off + 8] = d.getUTCSeconds();
        b[off + 9] = 0; b[off + 10] = 0; b[off + 11] = 0;
    }
    function extentAd(b, off, len, loc) { u32(b, off, len); u32(b, off + 4, loc); }
    function longAd(b, off, len, block, part) {
        u32(b, off, len); u32(b, off + 4, block); u16(b, off + 8, part || 0);
        for (var i = 10; i < 16; i++) b[off + i] = 0;
    }

    // ── the volume structures ──────────────────────────────────────────────
    function volumeRecognition() {
        // BEA01 / NSR02 / TEA01, one per block, from block 16. Without it an
        // OS may not recognise the medium as UDF at all even though the anchor
        // is where the reader looks.
        var out = [];
        [['BEA01', 0], ['NSR02', 0], ['TEA01', 0]].forEach(function (v) {
            var b = new Uint8Array(BLOCK);
            b[0] = v[1];
            ascii(b, 1, v[0], 5);
            b[6] = 1;
            out.push(b);
        });
        return out;
    }

    function primaryVolume(loc, seq, volId, setId, now) {
        var b = new Uint8Array(BLOCK);
        u32(b, 16, seq);
        u32(b, 20, 0);
        dstring(b, 24, volId, 32);
        u16(b, 56, 1); u16(b, 58, 1);              // volume 1 of 1
        u16(b, 60, 2); u16(b, 62, 2);              // interchange level 2
        u32(b, 64, 1); u32(b, 68, 1);
        dstring(b, 72, setId, 128);
        charspec(b, 200); charspec(b, 264);
        extentAd(b, 328, 0, 0); extentAd(b, 336, 0, 0);
        regid(b, 344, '*Timbrica UDF', 0, false);
        timestamp(b, 376, now);
        regid(b, 388, '*Timbrica UDF', 0, false);
        u32(b, 484, 0); u16(b, 488, 0);
        tag(b, 0, 1, loc, 512 - 16);
        return b;
    }

    function implUse(loc, seq, lvId) {
        var b = new Uint8Array(BLOCK);
        u32(b, 16, seq);
        regid(b, 20, '*UDF LV Info', 0, true);
        charspec(b, 52);
        dstring(b, 116, lvId, 128);
        dstring(b, 244, '', 36); dstring(b, 280, '', 36); dstring(b, 316, '', 36);
        regid(b, 352, '*Timbrica UDF', 0, false);
        tag(b, 0, 4, loc, 512 - 16);
        return b;
    }

    function partition(loc, seq, start, length) {
        var b = new Uint8Array(BLOCK);
        u32(b, 16, seq);
        u16(b, 20, 1);                              // allocated
        u16(b, 22, 0);                              // partition number 0
        regid(b, 24, '+NSR02', 0, false);
        // 56..183 partition contents use = partition header descriptor; a
        // read-only image has no space tables, so it stays zero.
        u32(b, 184, 1);                             // access type: read-only
        u32(b, 188, start);
        u32(b, 192, length);
        regid(b, 196, '*Timbrica UDF', 0, false);
        tag(b, 0, 5, loc, 512 - 16);
        return b;
    }

    function logicalVolume(loc, seq, lvId, fsdBlock, lvidLoc, lvidLen) {
        var b = new Uint8Array(BLOCK);
        u32(b, 16, seq);
        charspec(b, 20);
        dstring(b, 84, lvId, 128);
        u32(b, 212, BLOCK);
        regid(b, 216, '*OSTA UDF Compliant', 0, true);
        longAd(b, 248, BLOCK, fsdBlock, 0);         // where the File Set lives
        u32(b, 264, 6);                             // map table length
        u32(b, 268, 1);                             // one partition map
        regid(b, 272, '*Timbrica UDF', 0, false);
        extentAd(b, 432, lvidLen, lvidLoc);
        b[440] = 1; b[441] = 6; u16(b, 442, 1); u16(b, 444, 0);   // type-1 map
        tag(b, 0, 6, loc, 446 - 16);
        return b;
    }

    function unallocatedSpace(loc, seq) {
        var b = new Uint8Array(BLOCK);
        u32(b, 16, seq);
        u32(b, 20, 0);
        tag(b, 0, 7, loc, 24 - 16);
        return b;
    }
    function terminating(loc) {
        var b = new Uint8Array(BLOCK);
        tag(b, 0, 8, loc, 512 - 16);
        return b;
    }

    function integrity(loc, partLength, nFiles, nDirs, nextUniqueId, now) {
        var b = new Uint8Array(BLOCK);
        timestamp(b, 16, now);
        u32(b, 28, 1);                              // close integrity
        extentAd(b, 32, 0, 0);
        u64(b, 40, nextUniqueId);
        u32(b, 72, 1);                              // one partition
        u32(b, 76, 46);                             // implementation use length
        u32(b, 80, 0);                              // free space: none
        u32(b, 84, partLength);
        regid(b, 88, '*Timbrica UDF', 0, false);
        u32(b, 120, nFiles);
        u32(b, 124, nDirs);
        u16(b, 128, 0x0102);                        // minimum read revision
        u16(b, 130, 0x0102);
        u16(b, 132, 0x0102);
        tag(b, 0, 9, loc, 134 - 16);
        return b;
    }

    function anchor(loc, mvdsLoc, mvdsLen, rvdsLoc) {
        var b = new Uint8Array(BLOCK);
        extentAd(b, 16, mvdsLen, mvdsLoc);
        extentAd(b, 24, mvdsLen, rvdsLoc);
        tag(b, 0, 2, loc, 512 - 16);
        return b;
    }

    function fileSet(loc, rootBlock, lvId, fsId, now) {
        var b = new Uint8Array(BLOCK);
        timestamp(b, 16, now);
        u16(b, 28, 3); u16(b, 30, 3);
        u32(b, 32, 1); u32(b, 36, 1);
        u32(b, 40, 0); u32(b, 44, 0);
        charspec(b, 48);
        dstring(b, 112, lvId, 128);
        charspec(b, 240);
        dstring(b, 304, fsId, 32);
        dstring(b, 336, '', 32); dstring(b, 368, '', 32);
        longAd(b, 400, BLOCK, rootBlock, 0);        // ⭐ the root ICB
        regid(b, 416, '*OSTA UDF Compliant', 0, true);
        longAd(b, 448, 0, 0, 0); longAd(b, 464, 0, 0, 0);
        tag(b, 0, 256, loc, 512 - 16);
        return b;
    }

    /* File Entry. `extents` are {block, length} in partition blocks; a file
       larger than one extent gets several, which is not an edge case here —
       a DVD VOB reaches the 1 GiB cap the DVD spec itself imposes. */
    function fileEntry(loc, isDir, size, extents, linkCount, uniqueId, now) {
        var adLen = extents.length * 8;
        var total = 176 + adLen;
        var b = new Uint8Array(BLOCK);
        // ICB tag
        u32(b, 16, 0);
        u16(b, 20, 4);                              // strategy 4
        u16(b, 24, 1);                              // one entry
        b[27] = isDir ? 4 : 5;                      // file type
        u32(b, 28, 0); u16(b, 32, 0);               // parent ICB: none
        u16(b, 34, 0);                              // flags: short_ad, not sorted
        u32(b, 36, 0xffffffff); u32(b, 40, 0xffffffff);
        u32(b, 44, isDir ? 0x14a5 : 0x14a5);        // r-x for everyone
        u16(b, 48, linkCount);
        b[50] = 0; b[51] = 0; u32(b, 52, 0);
        u64(b, 56, size);
        u64(b, 64, Math.ceil(size / BLOCK));
        timestamp(b, 72, now); timestamp(b, 84, now); timestamp(b, 96, now);
        u32(b, 108, 1);
        longAd(b, 112, 0, 0, 0);
        regid(b, 128, '*Timbrica UDF', 0, false);
        u64(b, 160, uniqueId);
        u32(b, 168, 0);
        u32(b, 172, adLen);
        for (var i = 0; i < extents.length; i++) {
            u32(b, 176 + i * 8, extents[i].length);   // top 2 bits 0 = recorded
            u32(b, 180 + i * 8, extents[i].block);
        }
        tag(b, 0, 261, loc, total - 16);
        return b;
    }

    /* File Identifier Descriptor — one per child, plus a parent entry that
       every directory must open with. Length is padded to 4 bytes and the CRC
       covers the padding, so the two must be computed in that order. */
    function fid(name, isDir, isParent, icbBlock, uniqueId, loc) {
        var nameLen = isParent ? 0 : (1 + name.length);
        var base = 38 + nameLen;
        var total = Math.ceil(base / 4) * 4;
        var b = new Uint8Array(total);
        u16(b, 16, 1);                              // file version 1
        b[18] = (isDir ? 0x02 : 0) | (isParent ? 0x08 : 0);
        b[19] = nameLen;
        longAd(b, 20, BLOCK, icbBlock, 0);
        u16(b, 36, 0);                              // implementation use length
        if (!isParent) {
            b[38] = 8;                              // 8-bit compressed unicode
            for (var i = 0; i < name.length; i++) b[39 + i] = name.charCodeAt(i) & 255;
        }
        tag(b, 0, 257, loc, total - 16);
        return b;
    }

    /* Where each of a directory's FIDs goes. UDF forbids a File Identifier
       Descriptor from crossing a block boundary, so one that would is pushed to
       the start of the next block and the gap left zero. Both the layout pass
       and the writing pass call this, so they cannot disagree about the size. */
    function dirSlots(d) {
        var slots = [], off = 0;
        function place(len) {
            if ((off % BLOCK) + len > BLOCK) off = Math.ceil(off / BLOCK) * BLOCK;
            slots.push({ off: off, len: len });
            off += len;
        }
        place(40);                                   // the parent entry, padded to 4
        d.order.forEach(function (s) { place(Math.ceil((38 + 1 + s.name.length) / 4) * 4); });
        d.files.forEach(function (f) { place(Math.ceil((38 + 1 + f.name.length) / 4) * 4); });
        return { slots: slots, size: off };
    }

    // ── ISO 9660 bridge ──────────────────────────────────────────────────────
    /* A DVD-Video disc is a "UDF Bridge": the same files readable through UDF AND
       through ISO 9660. Set-top players differ in which filesystem they trust, so
       a spec disc carries both. These helpers emit a minimal ISO 9660 volume
       whose directory records point at the SAME absolute sectors the UDF File
       Entries do — the file data is placed once and shared. */
    function bothLE16BE(b, o, v) { b[o] = v & 255; b[o + 1] = (v >>> 8) & 255; b[o + 2] = (v >>> 8) & 255; b[o + 3] = v & 255; }
    function le32(b, o, v) { b[o] = v & 255; b[o + 1] = (v >>> 8) & 255; b[o + 2] = (v >>> 16) & 255; b[o + 3] = (v >>> 24) & 255; }
    function be32(b, o, v) { b[o] = (v >>> 24) & 255; b[o + 1] = (v >>> 16) & 255; b[o + 2] = (v >>> 8) & 255; b[o + 3] = v & 255; }
    function both32(b, o, v) { le32(b, o, v); be32(b, o + 4, v); }
    function a2(b, o, s, len) { for (var i = 0; i < len; i++) b[o + i] = i < s.length ? (s.charCodeAt(i) & 127) : 0x20; }
    function isoRecDate(b, o, d) {
        b[o] = d.getUTCFullYear() - 1900; b[o + 1] = d.getUTCMonth() + 1; b[o + 2] = d.getUTCDate();
        b[o + 3] = d.getUTCHours(); b[o + 4] = d.getUTCMinutes(); b[o + 5] = d.getUTCSeconds(); b[o + 6] = 0;
    }
    function isoDecDate(b, o, d) {
        function p(n, w) { var s = '' + n; while (s.length < w) s = '0' + s; return s; }
        a2(b, o, p(d.getUTCFullYear(), 4) + p(d.getUTCMonth() + 1, 2) + p(d.getUTCDate(), 2) +
             p(d.getUTCHours(), 2) + p(d.getUTCMinutes(), 2) + p(d.getUTCSeconds(), 2) + '00', 16);
        b[o + 16] = 0;
    }
    /* One ISO 9660 directory record. `name`: 0 for '.', 1 for '..', or a string
       identifier (NAME.EXT;1 for files, plain for directories). */
    function isoDirRecord(extent, size, isDir, name, now) {
        var special = (name === 0 || name === 1);
        var nameLen = special ? 1 : name.length;
        var recLen = 33 + nameLen; if (recLen & 1) recLen++;
        var b = new Uint8Array(recLen);
        b[0] = recLen; b[1] = 0;
        both32(b, 2, extent); both32(b, 10, size);
        isoRecDate(b, 18, now);
        b[25] = isDir ? 0x02 : 0x00;
        bothLE16BE(b, 28, 1);              // volume sequence number
        b[32] = nameLen;
        if (special) b[33] = name;         // 0x00 = '.', 0x01 = '..'
        else for (var i = 0; i < name.length; i++) b[33 + i] = name.charCodeAt(i) & 127;
        return b;
    }
    /* ISO 9660 8.3-ish identifier, matching _isoFilename so both views agree. */
    function isoName(raw) {
        var u = String(raw).toUpperCase();
        var dot = u.lastIndexOf(".");
        var clean = function (s) { return s.replace(/[^A-Z0-9_]/g, "_").replace(/^_+|_+$/g, ""); };
        var base = clean(dot >= 0 ? u.slice(0, dot) : u) || "FILE";
        var ext = dot >= 0 ? clean(u.slice(dot + 1)) : "";
        if (base.length > 30) base = base.slice(0, 30);
        if (ext.length > 30) ext = ext.slice(0, 30);
        return (ext ? base + "." + ext : base) + ";1";
    }

    /* Emit the ISO 9660 view of an already-laid-out disc: PVD at sector 16, the
       set terminator at 17, and path tables + directory extents placed in the
       free region, every record pointing at the SAME absolute data sectors the
       UDF File Entries use. Supports root + one level of subdirectories (a
       DVD-Video disc is root → VIDEO_TS → files), which also covers root files.
       Returns { sectors: {absSector: bytes}, endSector }. */
    function buildIsoBridge(dirs, absFn, totalSectors, volumeId, now, isoBase) {
        var out = {};
        // Directory numbers for the path table: root = 1, subdirs follow.
        dirs.forEach(function (d, i) { d.isoNum = i + 1; });
        // Reserve: L path table, M path table, then one+ extent per directory.
        var cur = isoBase + 2;
        dirs.forEach(function (d) {
            var recs = [isoDirRecord(0, 0, true, 0, now), isoDirRecord(0, 0, true, 1, now)];
            d.order.forEach(function (s) { recs.push(isoDirRecord(0, 0, true, isoName(s.name).replace(/;1$/, ''), now)); });
            d.files.forEach(function (f) { recs.push(isoDirRecord(0, 0, false, isoName(f.name), now)); });
            // Records may not cross a sector boundary.
            var used = 0, blocks = 1;
            recs.forEach(function (r) { if ((used % BLOCK) + r.length > BLOCK) used = Math.ceil(used / BLOCK) * BLOCK; used += r.length; });
            blocks = Math.max(1, Math.ceil(used / BLOCK));
            d.isoExtent = cur; d.isoBlocks = blocks; d.isoSize = blocks * BLOCK;
            cur += blocks;
        });
        // Now write each directory extent with real LBAs.
        dirs.forEach(function (d) {
            var buf = new Uint8Array(d.isoSize), off = 0;
            var recs = [];
            recs.push(isoDirRecord(d.isoExtent, d.isoSize, true, 0, now));                        // '.'
            var parent = d.parent || d;
            recs.push(isoDirRecord(parent.isoExtent, parent.isoSize, true, 1, now));               // '..'
            d.order.forEach(function (s) { recs.push(isoDirRecord(s.isoExtent, s.isoSize, true, isoName(s.name).replace(/;1$/, ''), now)); });
            d.files.forEach(function (f) { recs.push(isoDirRecord(absFn(f.dataBlock), f.size, false, isoName(f.name), now)); });
            recs.forEach(function (r) {
                if ((off % BLOCK) + r.length > BLOCK) off = Math.ceil(off / BLOCK) * BLOCK;
                buf.set(r, off); off += r.length;
            });
            for (var i = 0; i < d.isoBlocks; i++) out[d.isoExtent + i] = buf.subarray(i * BLOCK, (i + 1) * BLOCK);
        });
        // Path tables (L little-endian, M big-endian): one entry per directory.
        function pathTable(be) {
            var size = 0;
            dirs.forEach(function (d) { var nl = d.parent ? isoName(d.name).replace(/;1$/, '').length : 1; size += 8 + nl + (nl & 1); });
            var b = new Uint8Array(Math.ceil(size / BLOCK) * BLOCK), o = 0;
            dirs.forEach(function (d) {
                var nm = d.parent ? isoName(d.name).replace(/;1$/, '') : null;
                var nl = nm ? nm.length : 1;
                b[o] = nl; b[o + 1] = 0;
                if (be) be32(b, o + 2, d.isoExtent); else le32(b, o + 2, d.isoExtent);
                var pnum = (d.parent || d).isoNum;
                if (be) { b[o + 6] = (pnum >> 8) & 255; b[o + 7] = pnum & 255; } else { b[o + 6] = pnum & 255; b[o + 7] = (pnum >> 8) & 255; }
                if (nm) for (var i = 0; i < nm.length; i++) b[o + 8 + i] = nm.charCodeAt(i) & 127; else b[o + 8] = 0;
                o += 8 + nl + (nl & 1);
            });
            return { buf: b, size: size, blocks: b.length / BLOCK };
        }
        var lpt = pathTable(false), mpt = pathTable(true);
        out[isoBase] = lpt.buf.subarray(0, BLOCK);         // L path table (fits one sector for a DVD)
        out[isoBase + 1] = mpt.buf.subarray(0, BLOCK);     // M path table

        // Primary Volume Descriptor at 16.
        var pvd = new Uint8Array(BLOCK);
        pvd[0] = 1; a2(pvd, 1, 'CD001', 5); pvd[6] = 1;
        a2(pvd, 8, 'LINUX', 32);                            // system id (a-chars)
        a2(pvd, 40, volumeId, 32);                          // volume id
        both32(pvd, 80, totalSectors);                     // volume space size
        bothLE16BE(pvd, 120, 1);                           // volume set size
        bothLE16BE(pvd, 124, 1);                           // volume sequence number
        bothLE16BE(pvd, 128, BLOCK);                       // logical block size
        both32(pvd, 132, lpt.size);                        // path table size
        le32(pvd, 140, isoBase);                           // L path table location
        be32(pvd, 148, isoBase + 1);                       // M path table location
        var rootRec = isoDirRecord(dirs[0].isoExtent, dirs[0].isoSize, true, 0, now);
        pvd.set(rootRec.subarray(0, 34), 156);             // root directory record (34 bytes)
        a2(pvd, 190, volumeId, 128);                       // volume set id
        a2(pvd, 318, 'TIMBRICA', 128);                     // publisher
        a2(pvd, 446, 'TIMBRICA', 128);                     // data preparer
        a2(pvd, 574, 'SURROUND-DISC', 128);                // application id
        for (var z = 702; z < 813; z++) pvd[z] = 0x20;     // copyright/abstract/biblio file ids
        isoDecDate(pvd, 813, now); isoDecDate(pvd, 830, now);
        isoDecDate(pvd, 847, now); isoDecDate(pvd, 864, now);
        pvd[881] = 1;                                       // file structure version
        out[16] = pvd;
        // Volume Descriptor Set Terminator at 17.
        var vdst = new Uint8Array(BLOCK); vdst[0] = 255; a2(vdst, 1, 'CD001', 5); vdst[6] = 1;
        out[17] = vdst;
        return { sectors: out, endSector: cur };
    }

    // ── builder ────────────────────────────────────────────────────────────
    function UdfBuilder(opts) {
        opts = opts || {};
        this.volumeId = String(opts.volumeId || 'UDF_VOLUME').toUpperCase().slice(0, 30);
        this.files = [];                            // {path, data, size}
        this.now = opts.date instanceof Date ? opts.date : new Date(0);
        this.bridge = opts.bridge !== false;        // emit the ISO 9660 view too
    }
    /* Paths are '/DIR/NAME' with the same shape IsoBuilder takes, so a caller
       can hand the same map to either writer. */
    UdfBuilder.prototype.addFile = function (path, data) {
        var parts = String(path).replace(/^\/+/, '').split('/').filter(Boolean);
        if (!parts.length) throw new Error('udf_bad_path');
        var size = (data && data.byteLength != null) ? data.byteLength
                 : (data && data.size != null) ? data.size : 0;
        this.files.push({ parts: parts, data: data, size: size });
        return this;
    };

    UdfBuilder.prototype.build = function () {
        var self = this, now = this.now;

        // 1. Directory tree from the paths.
        var root = { name: '', dirs: {}, files: [], order: [] };
        this.files.forEach(function (f) {
            var d = root;
            for (var i = 0; i < f.parts.length - 1; i++) {
                var n = f.parts[i];
                if (!d.dirs[n]) { d.dirs[n] = { name: n, dirs: {}, files: [], order: [], parent: d }; d.order.push(d.dirs[n]); }
                d = d.dirs[n];
            }
            f.name = f.parts[f.parts.length - 1];
            d.files.push(f);
        });

        var dirs = [];
        (function walk(d) { dirs.push(d); d.order.forEach(walk); })(root);

        // 2. Block layout inside the partition. Metadata first, then the file
        //    data in the order it was added — on a DVD the read order is the
        //    order the player wants, so this is not arbitrary.
        var blk = 0;
        var fsdBlock = blk++;                        // File Set Descriptor
        var fsdTermBlock = blk++;                    // terminator of that sequence
        dirs.forEach(function (d) {
            d.entryBlock = blk++;
            d.dataBlock = blk;
            var lay = dirSlots(d);
            d.slots = lay.slots;
            d.dataBytes = lay.size;
            d.dataBlocks = Math.max(1, Math.ceil(lay.size / BLOCK));
            blk += d.dataBlocks;
        });
        var allFiles = [];
        dirs.forEach(function (d) { d.files.forEach(function (f) { allFiles.push(f); }); });
        allFiles.forEach(function (f) { f.entryBlock = blk++; });
        allFiles.forEach(function (f) {
            f.dataBlock = blk;
            f.blocks = Math.ceil(f.size / BLOCK);
            blk += f.blocks;
        });
        var partBlocks = blk;

        // 3. Absolute layout. 16 blocks of system area, the recognition
        //    sequence, two volume descriptor sequences, the integrity
        //    descriptor, the anchor at 256 (where readers look first).
        // On a bridge disc sector 16 is the ISO 9660 PVD, so the UDF volume
        // recognition sequence moves to 18; libdvdread finds UDF via the anchor
        // at 256 regardless, and Windows scans the sequence from 16 and reads
        // past the ISO descriptors to BEA01/NSR02/TEA01.
        var VRS = self.bridge ? 18 : 16;
        var MVDS = 32, RVDS = 48, LVID = 64, ANCHOR = 256, PART_START = 272;
        var VDS_BLOCKS = 16;

        var sectors = {};                            // absolute block → Uint8Array
        volumeRecognition().forEach(function (b, i) { sectors[VRS + i] = b; });

        var nFiles = allFiles.length, nDirs = dirs.length;
        var nextUnique = nFiles + nDirs + 1;
        [MVDS, RVDS].forEach(function (base) {
            sectors[base + 0] = primaryVolume(base + 0, 0, self.volumeId, self.volumeId, now);
            sectors[base + 1] = implUse(base + 1, 1, self.volumeId);
            sectors[base + 2] = partition(base + 2, 2, PART_START, partBlocks);
            sectors[base + 3] = logicalVolume(base + 3, 3, self.volumeId, fsdBlock, LVID, BLOCK * 2);
            sectors[base + 4] = unallocatedSpace(base + 4, 4);
            sectors[base + 5] = terminating(base + 5);
        });
        sectors[LVID] = integrity(LVID, partBlocks, nFiles, nDirs, nextUnique, now);
        sectors[LVID + 1] = terminating(LVID + 1);

        // 4. Partition contents.
        var abs = function (b) { return PART_START + b; };
        sectors[abs(fsdBlock)] = fileSet(fsdBlock, dirs[0].entryBlock, this.volumeId, this.volumeId, now);
        sectors[abs(fsdTermBlock)] = terminating(fsdTermBlock);

        var uid = 0;
        dirs.forEach(function (d) {
            var linkCount = 1 + d.order.length + (d.parent ? 1 : 0);
            sectors[abs(d.entryBlock)] = fileEntry(d.entryBlock, true, d.dataBytes,
                [{ block: d.dataBlock, length: d.dataBytes }], linkCount, ++uid, now);
            // directory contents, at the offsets the layout pass reserved
            var buf = new Uint8Array(d.dataBlocks * BLOCK);
            var children = [{ name: '', dir: true, parent: true, icb: (d.parent || d).entryBlock }]
                .concat(d.order.map(function (s) { return { name: s.name, dir: true, icb: s.entryBlock }; }))
                .concat(d.files.map(function (f) { return { name: f.name, dir: false, icb: f.entryBlock }; }));
            children.forEach(function (c, ix) {
                var slot = d.slots[ix];
                var e = fid(c.name, c.dir, !!c.parent, c.icb, 0, d.dataBlock + Math.floor(slot.off / BLOCK));
                buf.set(e, slot.off);
            });
            for (var i = 0; i < d.dataBlocks; i++) sectors[abs(d.dataBlock + i)] = buf.subarray(i * BLOCK, (i + 1) * BLOCK);
        });

        allFiles.forEach(function (f) {
            var extents = [], left = f.size, at = f.dataBlock;
            while (left > MAX_EXTENT) { extents.push({ block: at, length: MAX_EXTENT }); at += MAX_EXTENT / BLOCK; left -= MAX_EXTENT; }
            extents.push({ block: at, length: left });
            sectors[abs(f.entryBlock)] = fileEntry(f.entryBlock, false, f.size, extents, 1, ++uid, now);
        });

        // 5. Anchors: the one readers try first, and one on the last block —
        //    the pair the spec asks for on a sequentially-written volume.
        var total = PART_START + partBlocks;
        sectors[ANCHOR] = anchor(ANCHOR, MVDS, VDS_BLOCKS * BLOCK, RVDS);
        sectors[total] = anchor(total, MVDS, VDS_BLOCKS * BLOCK, RVDS);
        total += 1;

        // The ISO 9660 view: PVD at 16, terminator at 17, path tables + directory
        // extents in the free region (66+, after the UDF LVID) pointing at the
        // same file-data sectors. Makes the image a real UDF Bridge disc.
        if (self.bridge) {
            var isoOut = buildIsoBridge(dirs, abs, total, self.volumeId, now, 66);
            Object.keys(isoOut.sectors).forEach(function (k) { sectors[+k] = isoOut.sectors[k]; });
        }

        // 6. Assemble. File payloads go in by REFERENCE — a DVD image is
        //    gigabytes and must never be copied into one array.
        var pieces = [], cursor = 0;
        function padTo(block) {
            if (block > cursor) { pieces.push(new Uint8Array((block - cursor) * BLOCK)); cursor = block; }
        }
        var metaBlocks = Object.keys(sectors).map(Number).sort(function (a, b) { return a - b; });
        var dataAt = {};
        allFiles.forEach(function (f) { dataAt[abs(f.dataBlock)] = f; });
        var stops = metaBlocks.concat(Object.keys(dataAt).map(Number)).sort(function (a, b) { return a - b; });
        for (var i = 0; i < stops.length; i++) {
            var at = stops[i];
            if (at < cursor) continue;
            padTo(at);
            if (sectors[at]) { pieces.push(sectors[at]); cursor = at + 1; }
            if (dataAt[at]) {
                var f = dataAt[at];
                pieces.push(f.data);
                var pad = f.blocks * BLOCK - f.size;
                if (pad > 0) pieces.push(new Uint8Array(pad));
                cursor = at + f.blocks;
            }
        }
        padTo(total);

        return {
            blob: new Blob(pieces, { type: 'application/x-iso9660-image' }),
            size: total * BLOCK,
            totalSectors: total,
            layout: {
                anchor: ANCHOR, partitionStart: PART_START, partitionBlocks: partBlocks,
                files: allFiles.map(function (f) { return { name: f.name, block: abs(f.dataBlock), size: f.size }; })
            }
        };
    };

    w.__DiskImage = w.__DiskImage || {};
    w.__DiskImage.UdfBuilder = UdfBuilder;
})(window);
